import Form, {DatePickerEditorField, RegularEditorField, SelectEditorField} from '../utils/forms/Form';
import * as React from 'react';
import {JSX, useState} from 'react';
import * as Yup from 'yup';
import dayjs, {Dayjs} from 'dayjs';
import Decimal from 'decimal.js';
import {Account} from '../types';

export type TransferDTO = {
    fromAccountPublicId?: string;
    fromAmount: Decimal;
    fromCurrency?: string;
    toAccountPublicId?: string;
    toAmount: Decimal;
    toCurrency?: string;
    day: Dayjs | null;
    description: string;
};

function isZeroAmount(amount: Decimal.Value): boolean {
    return new Decimal(amount || 0).isZero();
}

export const TRANSFER_FORM_PROPERTIES = (
    transfer: TransferDTO,
    accounts: Account[],
    restrictToDates: Dayjs[],
    alwaysEditable: boolean = false,
    initialTransfer: TransferDTO = transfer,
    descriptionEditable: boolean = true,
    dateEditable?: boolean
) => {
    const areAllCurrenciesSet = !!transfer.fromCurrency && !!transfer.toCurrency;
    const transferWithConversion = areAllCurrenciesSet && transfer.fromCurrency !== transfer.toCurrency;
    return {
        presentation: 'dialog' as const,
        validationSchema: Yup.object({
            fromAccountPublicId: Yup.string().required('Wymagana'),
            toAccountPublicId: Yup.string()
                .notOneOf([Yup.ref('fromAccountPublicId')], 'Konto docelowe musi być inne niż źródłowe')
                .required('Wymagana'),
            fromAmount: Yup.number().required('Wymagana'),
            toAmount: Yup.number().required('Wymagana'),
            day: Yup.object().required('Wymagana'),
            description: Yup.string().required('Wymagana'),
        }),
        initialValues: {
            fromAccountPublicId: transfer.fromAccountPublicId,
            toAccountPublicId: transfer.toAccountPublicId,
            fromAmount: transfer.fromAmount,
            toAmount: transfer.toAmount,
            day: transfer.day || (restrictToDates.length > 0 ? restrictToDates[restrictToDates.length - 1] : dayjs()),
            description: transfer.description,
        } as TransferDTO,
        fields: [
            {
                key: 'fromAccountPublicId',
                label: 'Z konta',
                type: 'SELECT',
                selectOptions: accounts.map(account => {
                    return {
                        key: account.publicId,
                        displayElement: <>{account.name + ' (' + account.currentBalance.currency.code + ')'}</>,
                    };
                }),
                editable: alwaysEditable || !initialTransfer.fromAccountPublicId,
            } as SelectEditorField,
            {
                key: 'toAccountPublicId',
                label: 'Na konto',
                type: 'SELECT',
                selectOptions: accounts
                    .filter(account => account.publicId !== transfer.fromAccountPublicId)
                    .map(account => {
                        return {
                            key: account.publicId,
                            displayElement: <>{account.name + ' (' + account.currentBalance.currency.code + ')'}</>,
                        };
                    }),
                editable: alwaysEditable || !initialTransfer.toAccountPublicId,
            } as SelectEditorField,
            {
                label: 'Kwota' + (transferWithConversion ? ' z ' : ''),
                type: 'NUMBER',
                key: 'fromAmount',
                additionalProps: {sx: {display: areAllCurrenciesSet ? 'block' : 'none'}},
                editable: alwaysEditable || isZeroAmount(initialTransfer.fromAmount),
            } as RegularEditorField,
            {
                label: 'Kwota na',
                type: 'NUMBER',
                key: 'toAmount',
                additionalProps: {sx: {display: areAllCurrenciesSet && transferWithConversion ? 'block' : 'none'}},
                editable: alwaysEditable || isZeroAmount(initialTransfer.toAmount),
            } as RegularEditorField,
            {
                label: 'Data',
                type: 'DATEPICKER',
                key: 'day',
                editable: dateEditable ?? (alwaysEditable || !initialTransfer.day),
                additionalProps: {
                    sx: {
                        width: '100%',
                        display: areAllCurrenciesSet ? 'block' : 'none',
                    },
                },
                restrictToDates: restrictToDates,
            } as DatePickerEditorField,
            {
                label: 'Opis',
                type: 'TEXTAREA',
                key: 'description',
                editable: descriptionEditable,
                additionalProps: {
                    minRows: 2,
                    sx: {display: areAllCurrenciesSet ? 'block' : 'none'},
                },
            } as RegularEditorField,
        ],
    };
};

export interface CreateTransferFormProps {
    accounts: Account[];
    transferToCreate: TransferDTO & {possibleDays: Dayjs[]};
    onClose: (transferToCreate: TransferDTO | null) => void;
    alwaysEditable?: boolean;
    descriptionEditable?: boolean;
    dateEditable?: boolean;
}

export function CreateTransferForm({
    accounts,
    transferToCreate,
    onClose,
    alwaysEditable = false,
    descriptionEditable = true,
    dateEditable,
}: CreateTransferFormProps): JSX.Element {
    const [formProperties, setFormProperties] = useState(
        TRANSFER_FORM_PROPERTIES(
            transferToCreate,
            accounts,
            transferToCreate.possibleDays,
            alwaysEditable,
            transferToCreate,
            descriptionEditable,
            dateEditable
        )
    );
    return (
        <Form
            onSave={transfer => {
                const fromCurrency: string =
                    accounts.find(a => a.publicId === transfer.fromAccountPublicId || '')?.currentBalance.currency
                        .code || '';
                const toCurrency: string =
                    accounts.find(a => a.publicId === transfer.toAccountPublicId || '')?.currentBalance.currency.code ||
                    '';
                if (fromCurrency === toCurrency) transfer.toAmount = transfer.fromAmount;
                onClose({
                    ...transfer,
                    fromAmount: new Decimal(transfer.fromAmount),
                    toAmount: new Decimal(transfer.toAmount),
                    day: dayjs(transfer.day),
                });
            }}
            onCancel={() => onClose(null)}
            onChange={transfer => {
                const fromCurrency: string =
                    accounts.find(a => a.publicId === transfer.fromAccountPublicId || '')?.currentBalance.currency
                        .code || '';
                const toCurrency: string =
                    accounts.find(a => a.publicId === transfer.toAccountPublicId || '')?.currentBalance.currency.code ||
                    '';
                setFormProperties(
                    TRANSFER_FORM_PROPERTIES(
                        {
                            ...transfer,
                            fromCurrency: fromCurrency,
                            toCurrency: toCurrency,
                            toAmount: fromCurrency === toCurrency ? transfer.fromAmount : transfer.toAmount,
                        },
                        accounts,
                        transferToCreate.possibleDays,
                        alwaysEditable,
                        transferToCreate,
                        descriptionEditable,
                        dateEditable
                    )
                );
            }}
            {...formProperties}
        />
    );
}
